import { Skeleton, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import type { OffersCarouselCategory } from '@ocx/data-offers';
import type { AltriaOffersNearbyStatus } from '@ocx/feat-local-offers';
import { CategorySelector, LocationSelectorController } from '@ocx/feat-local-offers';
import { OfferCard } from '@ocx/ui-offers';
import { NoDataView, SizingMultiplier } from '@ocx/ui';

import { ExternalBannerCardController } from '../external-banner/external-banner-card.controller';
import { OfferCardController } from '../offer-card/offer-card.controller';
import { OffersCarouselController } from './offers-carousel.controller';
import { messages } from './offers-catalog-body.messages';

import type { ReactNode } from 'react';

export type OffersCatalogBodyProps = {
  isLocalOffersFeatureEnabled: boolean;
  categories: OffersCarouselCategory[];
  selectedCategory: OffersCarouselCategory | null;
  onCategoryChange: (categoryId: string) => void;
  hasAltriaOffersNearby?: AltriaOffersNearbyStatus;
  /** e.g. Home featured strip — rendered after location selector, before category selector */
  featuredSlot?: ReactNode;
  isOffersEmpty?: boolean;
  isCategoryLoading?: boolean;
};

const SelectedCategoryContent = ({
  selectedCategory,
  hasAltriaOffersNearby,
  isCategoryLoading,
}: {
  selectedCategory: OffersCarouselCategory | null;
  hasAltriaOffersNearby?: AltriaOffersNearbyStatus;
  isCategoryLoading?: boolean;
}) => {
  if (!selectedCategory) {
    return null;
  }

  const { banners, offers } = selectedCategory;
  const hasContent = banners.length > 0 || offers.length > 0;
  // Cold load only: nothing to show yet. Revalidation keeps cached banners/offers visible.
  const showBannersSkeleton = isCategoryLoading && !hasContent;
  const isCategoryEmpty = !isCategoryLoading && !hasContent;

  if (showBannersSkeleton) {
    return (
      <Stack spacing={SizingMultiplier.sm} data-testid="external-banners-loading">
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={120} />
      </Stack>
    );
  }

  if (isCategoryEmpty) {
    return <NoDataView title={<FormattedMessage {...messages['offers-catalog-body.no-offers-available-title']} />} />;
  }

  return (
    <Stack data-testid="offers-list-container" spacing={SizingMultiplier.sm}>
      {banners.map((banner) => (
        <ExternalBannerCardController
          key={`${banner.bannerConfigurationUuid}_${banner.trackingId}`}
          banner={banner}
          hasAltriaOffersNearby={hasAltriaOffersNearby}
        />
      ))}
      {offers.map((offer) => (
        <OfferCardController key={offer.id} offer={offer}>
          {(props) => <OfferCard {...props} />}
        </OfferCardController>
      ))}
    </Stack>
  );
};

export const OffersCatalogBody = ({
  isLocalOffersFeatureEnabled,
  categories,
  selectedCategory,
  onCategoryChange,
  hasAltriaOffersNearby,
  featuredSlot,
  isOffersEmpty,
  isCategoryLoading,
}: OffersCatalogBodyProps) => {
  return isLocalOffersFeatureEnabled ? (
    <>
      <LocationSelectorController />
      {featuredSlot}
      <CategorySelector
        data-testid="offers-carousel-container"
        isSticky
        selectedCategory={selectedCategory}
        onCategoryChange={onCategoryChange}
        categories={categories}
      />
      {isOffersEmpty && (
        <NoDataView title={<FormattedMessage {...messages['offers-catalog-body.no-offers-available-title']} />} />
      )}
      <SelectedCategoryContent
        selectedCategory={selectedCategory}
        hasAltriaOffersNearby={hasAltriaOffersNearby}
        isCategoryLoading={isCategoryLoading}
      />
    </>
  ) : (
    <>
      {featuredSlot}
      <OffersCarouselController
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={onCategoryChange}
        hasAltriaOffersNearby={hasAltriaOffersNearby}
        isCategoryLoading={isCategoryLoading}
      />
      {isOffersEmpty && (
        <NoDataView title={<FormattedMessage {...messages['offers-catalog-body.no-offers-available-title']} />} />
      )}
    </>
  );
};
